import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Heart, ExternalLink, Gift, Coffee, Youtube, ShoppingBag, BookOpen, Mail, Home, Instagram, Sparkles } from "lucide-react";

/**
 * LinkInBio — Public link-in-bio menu page for Instagram, TikTok, etc.
 * URL: /links
 * Designed to be the single link in Temple's Instagram bio.
 *
 * Brand-locked: True Teal (#068fa2) + Sacred Deep (#042F2E) + Cream (#efeae7)
 */
const LINKS = [
  {
    icon: Gift,
    label: "📘 FREE · The I GET TO Workbook (12 pages)",
    sublabel: "Instant PDF · rewire your brain, one true word at a time",
    href: "/workbook",
    isExternal: false,
    accent: "#d4af6a",
    featured: true,
  },
  {
    icon: Sparkles,
    label: "✦ All Free Resources",
    sublabel: "Workbook, cards, journals, audio, blog — everything free in one place",
    href: "/free",
    isExternal: false,
    accent: "#068fa2",
  },
  {
    icon: BookOpen,
    label: "📖 The Book · Stop Saying \"I Have To.\" Start Living \"I GET TO.\"",
    sublabel: "Paperback · $19.99 · the whole practice in your hands",
    href: "/book",
    isExternal: false,
    accent: "#042F2E",
  },
  {
    icon: Gift,
    label: "🌱 FREE · 30-Day Validation Journal",
    sublabel: "Notice signs, synchronicities & divine validation",
    href: "/validation-journal",
    isExternal: false,
    accent: "#25a1c2",
  },
  {
    icon: Coffee,
    label: "☕ Tip Jar · Sustain & Maintain",
    sublabel: "Support the channel · energy exchange welcome",
    href: "https://buy.stripe.com/4gMfZaaQ43JRfyye98c7u0o",
    isExternal: true,
    accent: "#d4af6a",
  },
  {
    icon: Youtube,
    label: "📺 YouTube · Daily Tapped-In Card Pulls",
    sublabel: "Morning energy check-ins · subscribe + ring the bell",
    href: "https://www.youtube.com/@MindBodySpiritHygiene",
    isExternal: true,
    accent: "#068fa2",
  },
  {
    icon: ShoppingBag,
    label: "🛒 Shop · Card Decks · Workbooks · Calm Kits",
    sublabel: "Real tools for real women in the messy middle",
    href: "/",
    isExternal: false,
    accent: "#042F2E",
  },
  {
    icon: BookOpen,
    label: "📖 Blog · Real talk · Real tools · Real life",
    sublabel: "Articles on boundaries, anxiety, hygiene & the messy middle",
    href: "/blog",
    isExternal: false,
    accent: "#25a1c2",
  },
  {
    icon: Home,
    label: "🏠 Website · MindBodySpiritHygiene.com",
    sublabel: "The full home of the Mind Body Spirit HYGIENE™ Method",
    href: "/",
    isExternal: false,
    accent: "#042F2E",
  },
];

const SOCIAL = [
  { label: "Instagram", href: "https://instagram.com/mindbodyspirithygienetools", icon: Instagram },
  { label: "Pinterest", href: "https://pinterest.com/mindbodyspirithygiene", icon: Sparkles },
];

export default function LinkInBio() {
  useEffect(() => {
    document.title = "Temple Franklin · MBSH Method™ · Links";
  }, []);

  return (
    <div
      data-testid="link-in-bio-page"
      className="min-h-screen relative overflow-hidden"
      style={{
        background:
          "linear-gradient(180deg, #042F2E 0%, #068fa2 50%, #042F2E 100%)",
      }}
    >
      {/* Texture overlay */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(circle at 20% 10%, rgba(94, 234, 212, 0.12) 0%, transparent 50%), radial-gradient(circle at 80% 90%, rgba(212, 175, 106, 0.08) 0%, transparent 50%)",
        }}
      />

      <div className="relative max-w-md mx-auto px-5 py-12 sm:py-16">
        {/* Header — photo placeholder + name + tagline */}
        <header className="text-center mb-10" data-testid="bio-header">
          {/* Avatar circle */}
          <div className="w-28 h-28 mx-auto mb-5 rounded-full overflow-hidden border-2 border-[#5EEAD4]/40 shadow-2xl bg-[#efeae7] flex items-center justify-center">
            <img
              src="/downloads/MBSH-logo-TM-e4476e.png"
              alt="Temple Franklin · Mind Body Spirit HYGIENE Tools"
              className="w-full h-full object-cover"
            />
          </div>

          {/* Kicker */}
          <p
            className="text-[10px] tracking-[0.3em] font-sans font-semibold uppercase mb-3"
            style={{ color: "#5EEAD4" }}
            data-testid="bio-kicker"
          >
            Mind Body Spirit HYGIENE™
          </p>

          {/* Name */}
          <h1
            className="font-serif text-4xl text-white mb-2 leading-tight"
            data-testid="bio-name"
          >
            Temple Franklin
          </h1>

          {/* Tagline */}
          <p
            className="font-serif italic text-base text-[#5EEAD4] mb-1 leading-snug"
            data-testid="bio-tagline"
          >
            Real tools, for real women,<br />in the messy middle.
          </p>

          {/* Credentials sub-line */}
          <p
            className="text-[11px] tracking-wide font-sans text-white/70 mt-3"
            data-testid="bio-credentials"
          >
            Author · Evidential Medium · Intuitive
          </p>

          {/* Sign-off */}
          <div className="flex items-center justify-center gap-2 mt-4">
            <span className="text-[11px] tracking-[0.25em] uppercase font-sans text-[#d4af6a]">Welcome home</span>
            <Heart className="w-3 h-3 fill-[#d4af6a]" style={{ color: "#d4af6a" }} />
          </div>
        </header>

        {/* Gold divider */}
        <div className="flex items-center justify-center gap-3 mb-8">
          <div className="h-[1px] flex-1 max-w-[80px]" style={{ backgroundColor: "#d4af6a", opacity: 0.5 }} />
          <span style={{ color: "#d4af6a" }} className="text-xs">✦</span>
          <div className="h-[1px] flex-1 max-w-[80px]" style={{ backgroundColor: "#d4af6a", opacity: 0.5 }} />
        </div>

        {/* Links */}
        <nav className="space-y-3" aria-label="Resource links" data-testid="bio-links">
          {LINKS.map((link, idx) => {
            const Icon = link.icon;
            const buttonContent = (
              <div
                className={`relative group flex items-center gap-3 w-full px-4 py-4 rounded-2xl backdrop-blur-sm transition-all duration-300 hover:scale-[1.02] hover:-translate-y-0.5 ${
                  link.featured
                    ? "bg-[#efeae7] border-2 shadow-2xl"
                    : "bg-white/95 hover:bg-white border border-white/40"
                }`}
                style={
                  link.featured
                    ? { borderColor: "#d4af6a" }
                    : undefined
                }
              >
                {/* Icon */}
                <div
                  className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center"
                  style={{
                    backgroundColor: `${link.accent}15`,
                    color: link.accent,
                  }}
                >
                  <Icon className="w-5 h-5" />
                </div>

                {/* Label + sublabel */}
                <div className="flex-1 text-left min-w-0">
                  <p
                    className="font-sans font-semibold text-sm leading-tight truncate"
                    style={{ color: "#042F2E" }}
                  >
                    {link.label}
                  </p>
                  <p
                    className="font-sans text-[11px] mt-0.5 leading-tight truncate"
                    style={{ color: "#068fa2" }}
                  >
                    {link.sublabel}
                  </p>
                </div>

                {/* External arrow */}
                {link.isExternal && (
                  <ExternalLink className="w-3.5 h-3.5 flex-shrink-0 opacity-50" style={{ color: "#042F2E" }} />
                )}

                {/* Featured badge */}
                {link.featured && (
                  <span
                    className="absolute -top-2 -right-2 text-[9px] font-sans font-bold uppercase tracking-wider px-2 py-0.5 rounded-full"
                    style={{ backgroundColor: "#d4af6a", color: "#042F2E" }}
                  >
                    Free
                  </span>
                )}
              </div>
            );

            return link.isExternal ? (
              <a
                key={idx}
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                className="block"
                data-testid={`bio-link-${idx}`}
              >
                {buttonContent}
              </a>
            ) : (
              <Link
                key={idx}
                to={link.href}
                className="block"
                data-testid={`bio-link-${idx}`}
              >
                {buttonContent}
              </Link>
            );
          })}
        </nav>

        {/* Book launching soon banner */}
        <div
          className="mt-8 p-4 rounded-2xl border text-center"
          style={{
            backgroundColor: "rgba(212, 175, 106, 0.1)",
            borderColor: "rgba(212, 175, 106, 0.4)",
          }}
          data-testid="book-banner"
        >
          <p className="text-[10px] tracking-[0.3em] uppercase font-sans mb-1" style={{ color: "#d4af6a" }}>
            Coming Soon
          </p>
          <p className="font-serif italic text-white text-lg leading-tight">
            "I GET TO"
          </p>
          <p className="font-sans text-xs text-white/70 mt-1">
            The book · Launching 2026
          </p>
        </div>

        {/* Social icons */}
        <div className="flex items-center justify-center gap-5 mt-10" data-testid="bio-socials">
          {SOCIAL.map((s) => {
            const SIcon = s.icon;
            return (
              <a
                key={s.label}
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={s.label}
                className="w-10 h-10 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center hover:bg-white/20 transition-all hover:scale-110"
                data-testid={`bio-social-${s.label.toLowerCase()}`}
              >
                <SIcon className="w-4 h-4 text-white" />
              </a>
            );
          })}
        </div>

        {/* Footer */}
      </div>
    </div>
  );
}
